import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert, Box, CircularProgress } from "@mui/material";
import AttachmentLink from "../components/AttachmentLink";
import { DepartmentRecord } from "../components/ImplementationCard";
import { EvaluationSummary } from "../components/EvaluationFormCard";
import {
  VerificationSummary,
  verificationRecord,
} from "../components/VerificationCard";
import { getSuggestion } from "../api/ess";
import {
  displayCategory,
  resolveSuggestionUnit,
  suggestionEvaluations,
  suggestionImplementation,
  verifierDepartments,
  type AssignedReviewer,
  type Suggestion,
} from "../types/ess";
import { displayDepartment } from "../api/departments";
import { useDepartments } from "../hooks/useDepartments";
import { unitGroupLabel } from "../utils/departments";
import { reviewerSlotLabel } from "../utils/reviewers";
import { statusLabel, useWorkflowConfig } from "../hooks/useWorkflow";
import { formatDateTime } from "../utils/format";

// Print-only field block. Plain HTML elements with the document-style
// classes from index.css — no MUI Cards, no shadows, no UI affordances.
function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="print-field-label">{label}</div>
      <div className="print-field-value">{value ?? "—"}</div>
    </div>
  );
}

// Roster grouped by committee, in the order the committees appear on the
// suggestion. Committee labels come from the workflow config; the raw
// token is the fallback so a roster is never hidden by a config gap.
function committeeGroups(
  reviewers: AssignedReviewer[],
): { committee: string; reviewers: AssignedReviewer[] }[] {
  const byCommittee = new Map<string, AssignedReviewer[]>();
  reviewers.forEach((r) => {
    byCommittee.set(r.committee, [...(byCommittee.get(r.committee) ?? []), r]);
  });
  return [...byCommittee.entries()].map(([committee, rs]) => ({
    committee,
    reviewers: rs,
  }));
}

export default function SuggestionPrint() {
  const { id = "" } = useParams();

  const detail = useQuery({
    queryKey: ["ess-detail", id],
    queryFn: () => getSuggestion(id),
    enabled: !!id,
  });

  const { data: workflow } = useWorkflowConfig();
  const { data: departmentList } = useDepartments();
  // Same department -> unit lookup the register builds; a row only carries
  // a department name, so the unit is resolved from the catalog.
  const unitByDepartment = useMemo(() => {
    const m: Record<string, string> = {};
    (departmentList ?? []).forEach((d) => {
      if (d?.name) m[d.name] = unitGroupLabel(d);
    });
    return m;
  }, [departmentList]);

  useEffect(() => {
    if (detail.data) {
      const t = window.setTimeout(() => window.print(), 300);
      return () => window.clearTimeout(t);
    }
  }, [detail.data]);

  if (detail.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Could not load suggestion.{" "}
          {detail.error instanceof Error ? detail.error.message : ""}
        </Alert>
      </Box>
    );
  }

  const s: Suggestion = detail.data;
  const unit = resolveSuggestionUnit(s, unitByDepartment);
  const submitterDepartment =
    ((s.metadata as any)?.submitter_department as string | null | undefined) ||
    null;
  const attachmentIds = s.attachment_ids ?? [];
  const groups = committeeGroups(s.assigned_reviewers ?? []);
  const evaluations = suggestionEvaluations(s);
  const verification = verificationRecord(s);
  // Implementation is tracked per verifier department, so the print
  // section is driven by the SC's nomination list rather than by
  // whatever keys happen to exist under metadata.implementation.
  const implementationDepartments = verifierDepartments(evaluations.ssc);
  const implementationRecords =
    suggestionImplementation(s).departments ?? {};
  const notes = s.notes ?? [];

  return (
    <div className="print-root">
      <div className="print-page">
        <header className="print-header">
          <img
            src="/images/indorama/logo_dark.webp"
            alt="Indorama"
            style={{
              height: "32pt",
              marginBottom: "8pt",
              marginLeft: "-6pt",
              display: "block",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "16pt",
            }}
          >
            <div>
              <div className="print-subhead">
                Suggestions — {s.suggestion_number ?? "—"}
              </div>
              <h1 className="print-title">{s.title}</h1>
              <div className="print-meta">
                Status: {statusLabel(workflow, s.status)}
              </div>
            </div>
            <div className="print-meta" style={{ whiteSpace: "nowrap" }}>
              Printed {formatDateTime(new Date().toISOString())}
            </div>
          </div>
        </header>

        <section className="print-block">
          <h2 className="print-section">Submission Details</h2>
          <div className="print-grid">
            <Field label="Employee" value={s.employee_name} />
            <Field label="Employee ID" value={s.employee_id} />
            {submitterDepartment && (
              <Field
                label="Submitter Department"
                value={displayDepartment(submitterDepartment)}
              />
            )}
            <Field
              label="Suggestion Department"
              value={displayDepartment(s.department)}
            />
            <Field label="Unit" value={unit ?? "—"} />
            <Field label="Category" value={displayCategory(s)} />
            {s.submitted_on_behalf_of && (
              <Field
                label="Submitted on behalf of"
                value={s.submitted_on_behalf_of}
              />
            )}
            <Field label="Submitted" value={formatDateTime(s.created_at)} />
            <Field label="Last updated" value={formatDateTime(s.updated_at)} />
          </div>
        </section>

        <section>
          <h2 className="print-section">Description</h2>
          <p className="print-body">{s.description}</p>
        </section>

        {attachmentIds.length > 0 && (
          <section className="print-block">
            <h2 className="print-section">Attachments</h2>
            <div className="print-tiles">
              {attachmentIds.map((aid) => (
                <AttachmentLink key={aid} assetId={aid} />
              ))}
            </div>
          </section>
        )}

        {groups.length > 0 && (
          <section className="print-block">
            <h2 className="print-section">Committees</h2>
            {groups.map((g) => (
              <div key={g.committee} className="print-block">
                <div className="print-field-label">
                  {workflow?.committees?.[g.committee]?.label ??
                    g.committee.toUpperCase()}
                </div>
                {g.reviewers.map((r) => (
                  <div
                    key={`${r.user_id}-${r.slot ?? r.sub_role}`}
                    className="print-field-value"
                  >
                    {r.user_name ?? r.user_id} —{" "}
                    {reviewerSlotLabel(r, workflow?.committees)}
                  </div>
                ))}
              </div>
            ))}
          </section>
        )}

        {evaluations.wc && (
          <section className="print-block">
            <h2 className="print-section">WC Evaluation</h2>
            <EvaluationSummary committee="wc" evalRecord={evaluations.wc} />
          </section>
        )}

        {evaluations.ssc && (
          <section className="print-block">
            <h2 className="print-section">SC Evaluation</h2>
            <EvaluationSummary committee="ssc" evalRecord={evaluations.ssc} />
          </section>
        )}

        {implementationDepartments.length > 0 && (
          <section className="print-block">
            <h2 className="print-section">Implementation</h2>
            {implementationDepartments.map((dept) => (
              <div key={dept} className="print-block">
                <DepartmentRecord
                  department={dept}
                  record={implementationRecords[dept]}
                />
              </div>
            ))}
          </section>
        )}

        {verification && (
          <section className="print-block">
            <h2 className="print-section">Custodian Review & Closure</h2>
            <VerificationSummary record={verification} suggestion={s} />
          </section>
        )}

        {notes.length > 0 && (
          <section className="print-block">
            <h2 className="print-section">Notes</h2>
            {notes.map((n) => (
              <div key={n.id} className="print-history-row">
                <div className="print-history-meta">
                  {n.created_by_name ?? n.created_by ?? "Unknown"} ·{" "}
                  {formatDateTime(n.created_at)}
                </div>
                <p className="print-body">{n.note}</p>
              </div>
            ))}
          </section>
        )}

        {s.remarks && (
          <section className="print-block">
            <h2 className="print-section">Latest Remarks</h2>
            <p className="print-body">{s.remarks}</p>
          </section>
        )}
      </div>
    </div>
  );
}
